/** VS Code 風クイックオープンのための軽量ファジーマッチャ。外部依存なし。 */

export interface FuzzyMatch {
	/** 大きいほど良い。同点のタイブレークは呼び出し側 (パス長 → 辞書順など) で行う。 */
	score: number;
	/** text 内でマッチした文字インデックス (昇順)。 */
	indices: number[];
}

export interface HighlightRun {
	text: string;
	matched: boolean;
}

const BOUNDARY_CHARS = new Set(["/", ".", "_", "-", " "]);

/** query から始めて text 内で貪欲に部分列マッチを探す。見つからなければ null。 */
function greedySubsequence(query: string, text: string, from: number): number[] | null {
	const indices: number[] = [];
	let cursor = from;
	for (const ch of query) {
		const idx = text.indexOf(ch, cursor);
		if (idx === -1) return null;
		indices.push(idx);
		cursor = idx + 1;
	}
	return indices;
}

function scoreIndices(indices: readonly number[], text: string, basenameStart: number): number {
	let score = 0;
	for (let k = 0; k < indices.length; k++) {
		const i = indices[k];
		score += 1;
		if (k > 0 && indices[k - 1] === i - 1) score += 6;
		if (i === 0 || BOUNDARY_CHARS.has(text[i - 1])) score += 8;
		if (i >= basenameStart) score += 4;
	}
	return score;
}

/**
 * query が text の部分列 (大文字小文字無視) ならスコア付きで返す。部分列でなければ null。
 * 空白除去後の query が空文字列の場合は score 0 / indices [] で常にマッチする。
 */
export function fuzzyMatch(query: string, text: string): FuzzyMatch | null {
	const trimmed = query.trim();
	if (trimmed === "") return { score: 0, indices: [] };

	const q = trimmed.toLowerCase();
	const t = text.toLowerCase();

	// アンカー = 先頭 + 各 "/" 直後 (セグメント先頭)。それぞれを起点に部分列全体が
	// マッチするか試す。アンカー 0 は text が部分列を含む限り必ず成功するため、
	// 該当なしで null になることはない。
	const anchors = [0];
	for (let i = 0; i < t.length; i++) {
		if (t[i] === "/") anchors.push(i + 1);
	}
	const basenameStart = anchors.at(-1)!;

	let best: FuzzyMatch | null = null;
	for (const anchor of anchors) {
		const indices = greedySubsequence(q, t, anchor);
		if (indices === null) continue;
		const score = scoreIndices(indices, text, basenameStart);
		if (best === null || score > best.score) best = { score, indices };
	}
	return best;
}

/** indices を連続 run にまとめ、ハイライト描画しやすい形へ変換する。 */
export function toHighlightRuns(text: string, indices: readonly number[]): HighlightRun[] {
	const matchedSet = new Set(indices);
	const runs: HighlightRun[] = [];
	let i = 0;
	while (i < text.length) {
		const matched = matchedSet.has(i);
		let j = i + 1;
		while (j < text.length && matchedSet.has(j) === matched) j++;
		runs.push({ text: text.slice(i, j), matched });
		i = j;
	}
	return runs;
}
